/**
 * SourceCardV3Popover - Stepper-based popover content for citation sources.
 *
 * Pure presentational component: takes sources + stepper state, renders one
 * card at a time with prev/next navigation. All interaction (opening URLs,
 * previewing documents) is delegated via `onSourceSelect`.
 *
 * Used by CitationBadge in production and directly in Storybook for preview.
 * Figma: node 7520:32139
 */

import { Button } from '@heidi/design-system/button';
import { Separator } from '@heidi/design-system/separator';
import { getFaviconUrl } from '@heidi/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { SimpleCitation } from '../../contracts';
import { isFileCitation } from '../../contracts';
import { SourceCard } from './SourceCard';

interface SourceCardV3PopoverProps {
  sources: SimpleCitation[];
  /** Called when a user clicks the source card. Receives the source and its index. */
  onSourceSelect?: (source: SimpleCitation, index: number) => void;
  /** Whether the save/bookmark action is available. Defaults to true for web sources. */
  showSaveAction?: boolean;
  /** MOD-1726: `sources` includes a local one, so a `web` card is labelled too. */
  hasLocalSources?: boolean;
}

export function SourceCardV3Popover({
  sources,
  onSourceSelect,
  showSaveAction,
  hasLocalSources,
}: SourceCardV3PopoverProps) {
  const intl = useIntl();
  const [stepperIndex, setStepperIndex] = useState(0);
  const safeIndex = Math.min(stepperIndex, sources.length - 1);
  const source = sources[safeIndex];

  if (!source) return null;

  const isSourceFile = isFileCitation(source.source_type);
  // Save requires a URL (BE keys CPD sources by URL). Some integration partners
  // (e.g. EMGuidance, Vidal, Elsevier) return citations without a public URL,
  // so AND the parent flag with a URL check rather than using it as a fallback.
  const canSave =
    (showSaveAction ?? true) &&
    !isSourceFile &&
    source.source_type !== 'user_document' &&
    !!source.url;

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStepperIndex(safeIndex <= 0 ? sources.length - 1 : safeIndex - 1);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStepperIndex(safeIndex >= sources.length - 1 ? 0 : safeIndex + 1);
  };

  return (
    <div className="flex flex-col">
      {/* Stepper header */}
      <div className="flex items-center justify-between py-3 pl-2 pr-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            data-testid="source-stepper-prev"
            className="text-text-secondary"
            onClick={handlePrev}
          >
            <ChevronLeft className="rtl:-scale-x-100" />
          </Button>
          <span
            data-testid="source-stepper-counter"
            className="text-xs text-text-secondary"
          >
            {/* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                locales, so re-minting it to the hash would orphan every translation. */}
            {intl.formatMessage(
              { defaultMessage: '{current} of {total}', id: 'evidence.stepperCounter' },
              { current: safeIndex + 1, total: sources.length }
            )}
          </span>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            data-testid="source-stepper-next"
            className="text-text-secondary"
            onClick={handleNext}
          >
            <ChevronRight className="rtl:-scale-x-100" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {sources.slice(0, 3).map((s, i) => (
              <div
                key={s.url ?? s.source_id ?? i}
                className="size-5 overflow-hidden rounded-full border-2 border-surface bg-background-tertiary"
              >
                {s.url && !isFileCitation(s.source_type) ? (
                  // eslint-disable-next-line @next/next/no-img-element -- External dynamic favicons
                  <img
                    src={getFaviconUrl(s.url)}
                    alt=""
                    className="size-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
          <span className="text-xs text-text-secondary">
            {/* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                locales, so re-minting it to the hash would orphan every translation. */}
            {intl.formatMessage(
              {
                defaultMessage: '{count, plural, one {# source} other {# sources}}',
                id: 'evidence.sourceCount',
              },
              { count: sources.length }
            )}
          </span>
        </div>
      </div>

      <Separator />

      {/* Active source card — uses div instead of button to avoid nesting interactive elements (SourceCard contains <a> and <button>) */}
      <div
        role="button"
        tabIndex={0}
        className="w-full cursor-pointer p-4 text-left"
        onClick={() => onSourceSelect?.(source, safeIndex)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSourceSelect?.(source, safeIndex);
          }
        }}
      >
        <SourceCard
          {...source}
          sourceIndex={safeIndex}
          showSaveAction={canSave}
          hasLocalSources={hasLocalSources}
          variant="v3"
          onTitleClick={() => onSourceSelect?.(source, safeIndex)}
        />
      </div>
    </div>
  );
}
